(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (root && root.document && !root.K2CartShippingEstimator) {
    root.K2CartShippingEstimator = api;
    api.mount(root);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  var FALLBACK = 'Shipping estimate unavailable. Check your ZIP and state, or continue to checkout for final shipping and taxes.';
  var INITIAL = 'Shipping and taxes confirmed at checkout.';
  var INTERNATIONAL = 'Shipping to destinations outside the United States is confirmed at checkout.';

  function stable(value) {
    if (Array.isArray(value)) return value.map(stable);
    if (value && typeof value === 'object') {
      var result = {};
      Object.keys(value).sort().forEach(function (key) { result[key] = stable(value[key]); });
      return result;
    }
    return value;
  }

  function cartKey(cart) {
    return JSON.stringify(stable({
      token: cart.token, currency: cart.currency, total: cart.total_price,
      discounts: cart.cart_level_discount_applications,
      items: (cart.items || []).map(function (item) {
        return { key: item.key, id: item.variant_id || item.id, quantity: item.quantity,
          price: item.final_line_price, shipping: item.requires_shipping,
          properties: item.properties, plan: item.selling_plan_allocation };
      })
    }));
  }

  function normalizeDestination(destination) {
    return { country: destination.country === 'US' ? 'US' : 'OTHER',
      zip: String(destination.zip || '').trim(),
      province: String(destination.province || '').trim().toUpperCase() };
  }

  function parseRates(payload, currency, shopCurrency) {
    if (!payload || !Array.isArray(payload.shipping_rates) || !payload.shipping_rates.length) return [];
    var uncertainCurrency = false;
    var rates = payload.shipping_rates.map(function (rate) {
      var raw = rate && rate.price;
      if ((typeof raw !== 'number' && typeof raw !== 'string') || !/^\d+(?:\.\d+)?$/.test(String(raw))) return null;
      var amount = Number(raw);
      if (!Number.isFinite(amount) || amount < 0) return null;
      var declared = String(rate.currency || payload.currency || '').toUpperCase();
      if ((declared && declared !== currency) || (!declared && (currency !== 'USD' || shopCurrency !== 'USD'))) {
        uncertainCurrency = true;
        return null;
      }
      var name = String(rate.presentment_name || rate.name || 'Shipping');
      return { amount: amount, currency: currency, name: name,
        freight: /\b(freight|ltl|truck delivery|truck shipping)\b/i.test(name) };
    }).filter(Boolean);
    if (uncertainCurrency) throw new Error('currency');
    return rates.sort(function (a, b) { return a.amount - b.amount; });
  }

  function formatAmount(amount, currency, locale) {
    return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency, currencyDisplay: 'code' }).format(amount);
  }

  function createController(options) {
    var sequence = 0;
    var active = null;
    var cache = new Map();
    var routeRoot = String(options.routeRoot || '/').replace(/\/?$/, '/');
    var timeoutMs = options.timeoutMs || 10000;
    var now = options.now || Date.now;
    var render = options.render;
    var setTimer = options.setTimeout || setTimeout;
    var clearTimer = options.clearTimeout || clearTimeout;
    var lastDestination = null;

    function invalidate(message, keepCache) {
      sequence += 1;
      if (active) active.controller.abort();
      active = null;
      if (!keepCache) cache.clear();
      if (message) render({ status: 'idle', message: message });
    }

    async function quote(destination) {
      invalidate(null, true);
      var id = sequence;
      var address = normalizeDestination(destination);
      lastDestination = address;
      if (address.country !== 'US') {
        render({ status: 'unavailable', message: INTERNATIONAL });
        return;
      }
      if (!/^\d{5}(?:-\d{4})?$/.test(address.zip)) {
        render({ status: 'input', message: 'Enter a valid US ZIP code, such as 34953. You can also continue to checkout.' });
        return;
      }
      if (address.province && !/^[A-Z]{2}$/.test(address.province)) {
        render({ status: 'input', message: 'Use the two-letter state abbreviation, or leave state blank. Checkout remains available.' });
        return;
      }
      var request = { id: id, controller: new AbortController() };
      active = request;
      var timer;
      function current() { return active === request && sequence === id; }
      function ensureCurrent() { if (!current()) throw new Error('stale'); }
      async function get(path) {
        var response = await options.fetch(routeRoot + path, { method: 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json' }, signal: request.controller.signal });
        ensureCurrent();
        if (!response.ok) throw new Error('request');
        var data = await response.json();
        ensureCurrent();
        return data;
      }
      render({ status: 'loading', message: 'Checking shipping to US ' + address.zip + '…' });
      var deadline = new Promise(function (_, reject) {
        timer = setTimer(function () { request.controller.abort(); reject(new Error('timeout')); }, timeoutMs);
      });
      async function run() {
        var cart = await get('cart.js');
        var currency = String(cart.currency || '').toUpperCase();
        if (!/^[A-Z]{3}$/.test(currency)) throw new Error('currency');
        if (!Array.isArray(cart.items) || !cart.items.length) return { status: 'idle', message: INITIAL };
        if (cart.requires_shipping === false) return { status: 'ready', message: 'Shipping is not required for this cart. Your final total is confirmed at checkout.' };
        var fingerprint = cartKey(cart);
        var key = JSON.stringify([fingerprint, address.country, address.zip, address.province, currency, routeRoot]);
        var cached = cache.get(key);
        var rates;
        if (cached && now() - cached.at < 60000) rates = cached.rates;
        else {
          var params = new URLSearchParams({ 'shipping_address[country]': 'United States', 'shipping_address[zip]': address.zip });
          if (address.province) params.set('shipping_address[province]', address.province);
          rates = parseRates(await get('cart/shipping_rates.json?' + params.toString()), currency, options.shopCurrency);
        }
        var latest = await get('cart.js');
        if (cartKey(latest) !== fingerprint) return { status: 'stale', message: 'Your cart changed. Select Estimate shipping again for the updated cart, or continue to checkout.' };
        if (!rates.length) return { status: 'unavailable', message: FALLBACK };
        cache.set(key, { at: now(), rates: rates });
        var rate = rates[0];
        var amount = formatAmount(rate.amount, currency, options.locale);
        return { status: 'ready', rates: rates, currency: currency, key: key,
          message: (rate.freight ? 'Truck freight estimate' : 'Shipping estimate') + ' to US ' + address.zip + ': ' + amount + ' — ' + rate.name + '. Final shipping and taxes are confirmed at checkout.' };
      }
      try {
        var result = await Promise.race([run(), deadline]);
        if (current()) render(result);
      } catch (error) {
        if (current()) render({ status: 'unavailable', message: FALLBACK });
      } finally {
        clearTimer(timer);
        if (current()) active = null;
      }
    }
    return { quote: quote, invalidate: invalidate, destination: function () { return lastDestination; } };
  }

  async function locate(options) {
    var controller = new AbortController();
    var timer;
    var setTimer = options.setTimeout || setTimeout;
    var clearTimer = options.clearTimeout || clearTimeout;
    try {
      return await Promise.race([
        (async function () {
          var response = await options.fetch('https://ipapi.co/json/', { signal: controller.signal, credentials: 'omit', cache: 'no-store' });
          if (!response.ok) throw new Error('location');
          var geo = await response.json();
          if (!geo || !/^[A-Z]{2}$/.test(String(geo.country_code || ''))) throw new Error('location');
          if (geo.country_code !== 'US') return { country: 'OTHER', zip: '', province: '' };
          if (!/^\d{5}(?:-\d{4})?$/.test(String(geo.postal || ''))) throw new Error('postal');
          return normalizeDestination({ country: 'US', zip: geo.postal, province: String(geo.region_code || '').replace(/^US-/, '') });
        })(),
        new Promise(function (_, reject) { timer = setTimer(function () { controller.abort(); reject(new Error('location timeout')); }, options.timeoutMs || 2500); })
      ]);
    } finally { clearTimer(timer); controller.abort(); }
  }

  function mount(win) {
    var doc = win.document;
    var instances = new Map();
    var saved = null;
    function scan() {
      instances.forEach(function (instance, element) { if (!element.isConnected) { instance.controller.invalidate(); instances.delete(element); } });
      doc.querySelectorAll('[data-k2-cart-shipping]').forEach(function (element) {
        if (instances.has(element)) return;
        var form = element.querySelector('[data-k2-quote-form]');
        var country = element.querySelector('[data-k2-quote-country]');
        var zip = element.querySelector('[data-k2-quote-zip]');
        var province = element.querySelector('[data-k2-quote-state]');
        var status = element.querySelector('[data-k2-quote-status]');
        var button = element.querySelector('[data-k2-quote-button]');
        var locationButton = element.querySelector('[data-k2-quote-location]');
        if (!form || !country || !zip || !province || !status || !button || !locationButton) return;
        var edits = 0;
        var controller = createController({ fetch: win.fetch.bind(win), routeRoot: element.dataset.root,
          shopCurrency: element.dataset.shopCurrency, locale: element.dataset.locale,
          render: function (state) {
            if (!element.isConnected) return;
            if (status.textContent !== state.message) status.textContent = state.message;
            status.dataset.state = state.status;
            status.setAttribute('aria-busy', String(state.status === 'loading'));
            button.textContent = state.status === 'loading' ? 'Checking…' : 'Estimate shipping';
          } });
        function destination() { return { country: country.value, zip: zip.value, province: province.value }; }
        function setDestination(address) { country.value = address.country; zip.value = address.zip; province.value = address.province; }
        function changed() {
          edits += 1;
          saved = destination();
          controller.invalidate(country.value === 'US' ? INITIAL : INTERNATIONAL);
          zip.disabled = country.value !== 'US';
          province.disabled = country.value !== 'US';
        }
        if (saved) setDestination(saved);
        changed();
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          edits += 1;
          saved = destination();
          controller.quote(saved);
        });
        country.addEventListener('change', changed);
        zip.addEventListener('input', changed);
        province.addEventListener('input', changed);
        locationButton.addEventListener('click', async function () {
          controller.invalidate();
          var edit = edits;
          locationButton.disabled = true;
          status.textContent = 'Finding an approximate ZIP. You can enter your ZIP manually at any time.';
          try {
            var address = await locate({ fetch: win.fetch.bind(win) });
            if (edit !== edits || !element.isConnected) return;
            setDestination(address);
            changed();
            saved = destination();
            controller.quote(saved);
          } catch (error) {
            if (edit === edits && element.isConnected) status.textContent = 'Location unavailable. Enter your ZIP manually, or continue to checkout.';
          } finally { locationButton.disabled = false; }
        });
        instances.set(element, { controller: controller });
      });
    }
    function cartChanged() {
      instances.forEach(function (instance) { instance.controller.invalidate('Your cart changed. Select Estimate shipping again for the updated cart, or continue to checkout.'); });
      scan();
    }
    ['cart:updated', 'cart:refresh', 'cart:change', 'cart:changed', 'dispatch:cart-drawer:refresh', 'on:cart:after-merge', 'on:bfcache:load-restore', 'bndlr:cart_was_modified'].forEach(function (name) { doc.addEventListener(name, cartChanged); });
    var observer = new win.MutationObserver(function () { scan(); });
    observer.observe(doc.documentElement, { childList: true, subtree: true });
    doc.addEventListener('change', function (event) {
      if (event.target.closest && event.target.closest('#cart-drawer') && !event.target.closest('[data-k2-cart-shipping]')) cartChanged();
    });
    scan();
  }
  return { createController: createController, parseRates: parseRates, cartKey: cartKey, formatAmount: formatAmount,
    locate: locate, mount: mount, messages: { initial: INITIAL, fallback: FALLBACK, international: INTERNATIONAL } };
});
